import {
  type FileEditCompleteEdit,
  type FileEditCompleteImageEdit,
  type ProgrammaticMessageOptions,
} from '@air/feature-ag-ui-chat';

import { type AppNode } from '../../types';
import { getUrlFileExtension } from '../getUrlFileExtension';

// Local copy so this pure helper stays free of the @air/feature-ag-ui-chat
// runtime barrel (which pulls UI components and breaks unit-test resolution).
const VIDEO_EXTENSIONS = ['MP4', 'WEBM', 'MOV', 'M4V', 'OGV', 'AVI', 'MKV'];

const isImageEdit = (edit: FileEditCompleteEdit): edit is FileEditCompleteImageEdit => 'url' in edit;

const isVideoUrl = (url: string): boolean => getUrlFileExtension(url, VIDEO_EXTENSIONS) !== undefined;

export type InPlaceLayerEdit = {
  edit: FileEditCompleteImageEdit;
  targetNodeId: string;
};

export type ResolveInPlaceLayerEditParams = {
  /** The transform that produced the edit, when the run was programmatic. */
  actionName?: ProgrammaticMessageOptions['actionName'];
  edits: FileEditCompleteEdit[];
  nodes: AppNode[];
  /**
   * Replace the source even when it is not grouped. Crop-and-extend sets this so
   * the intersection-crop node becomes the outpaint result instead of appending.
   */
  replaceSourceNode?: boolean;
  /** The agent-reported source node id (local id or remoteId). */
  sourceNodeId: string | undefined;
};

/**
 * Decides whether an agent edit should replace its source layer in place.
 *
 * Returns the target node id + the single image edit when the result is a
 * single image and either:
 * - the source is a grouped image layer (edit text / remove background /
 *   prompt on a grouped layer — so the edit stays in the group), or
 * - `replaceSourceNode` is set (crop-and-extend: the intersection crop is
 *   the outpaint source and must not remain as a leftover node).
 *
 * Returns `undefined` otherwise, so the result appends: standalone images
 * without the flag, multi-edit results (smart resize), video results,
 * `extendBackground` without the flag (leave the source, like translate), or
 * when there's no resolvable source.
 *
 * A single image edit may carry a `group` field (the agent echoing the source's
 * group); that's still an in-place replace, not a new composition, so the group
 * field is intentionally ignored here.
 *
 * `translate` and pure `extendBackground` opt out entirely: both results are
 * only meaningful read against the original, so they append beside the source
 * even when that source is a grouped layer. Crop-and-extend still replaces
 * because it sets `replaceSourceNode` on the intersection-crop node.
 */
export const resolveInPlaceLayerEdit = ({
  actionName,
  edits,
  nodes,
  replaceSourceNode,
  sourceNodeId,
}: ResolveInPlaceLayerEditParams): InPlaceLayerEdit | undefined => {
  if (!sourceNodeId || actionName === 'translate') {
    return undefined;
  }

  // Pure extend appends so the original stays on the canvas (and follow-up
  // pills can target the new asset). Crop-and-extend sets replaceSourceNode
  // so the intersection-crop node becomes the outpaint result.
  if (actionName === 'extendBackground' && !replaceSourceNode) {
    return undefined;
  }

  const targetNode = nodes.find(
    (node) => (node.id === sourceNodeId || node.remoteId === sourceNodeId) && node.type === 'image',
  );

  if (!targetNode || (!targetNode.groupId && !replaceSourceNode)) {
    return undefined;
  }

  const edit = edits.length === 1 ? edits.filter(isImageEdit).find((e) => !isVideoUrl(e.url)) : undefined;

  if (!edit) {
    return undefined;
  }

  return { edit, targetNodeId: targetNode.id };
};
